<template>
    <div class="columns is-gapless h-100">
        <div class="column is-narrow box-shadow-v has-background-greyBG">
            <div class="is-flex has-text-primary p-2">
                <h6 class="is-subtitle-size is-primary pl-3 mr-4">
                    {{ $t("conference.web-cam") }}
                </h6>
                <Icon family="fal" name="fa-users-class" />
            </div>
            <div class="videos-container px-3 mx-2">
                <LocalParticipant
                    v-if="localTracks"
                    :participant="localParticipant"
                    :tracks="localTracks"
                    :userName="user.name"
                    :mutedAudio="mutedAudio"
                    :mutedVideo="mutedVideo"
                />
                <Participant
                    v-for="(participant, index) in participants"
                    :participant="participant"
                    :userName="getUserName(participant.identity)"
                    :userIdentification="
                        getUserIdentification(participant.identity)
                    "
                    :roomId="roomId"
                    :key="index"
                />
            </div>

            <div class="w-100 mt-3 is-justify-content-center is-inline-flex">
                <div>
                    <button
                        v-if="!mutedAudio"
                        class="button is-primary is-outlined is-radiusless mr-1"
                        @click="muteAudio"
                    >
                        <Icon family="fal" name="fa-microphone" />
                    </button>
                    <button
                        v-if="mutedAudio"
                        class="button is-primary is-outlined is-radiusless mr-1"
                        @click="unmuteAudio"
                    >
                        <Icon family="fal" name="fa-microphone-slash" />
                    </button>
                </div>
                <div>
                    <button
                        v-if="!mutedVideo"
                        class="button is-primary is-outlined is-radiusless mr-1"
                        @click="muteVideo"
                    >
                        <Icon family="fal" name="fa-video" />
                    </button>
                    <button
                        v-if="mutedVideo"
                        class="button is-primary is-outlined is-radiusless mr-1"
                        @click="unmuteVideo"
                    >
                        <Icon family="fal" name="fa-video-slash" />
                    </button>
                </div>
                <Clock />
            </div>
        </div>

        <Notes v-if="isOpenedNotes" />
        <Historical v-if="isOpenedHistorical" />

        <div class="column">
            <ColaborativeBoard :signalRSocket="signalRSocket" />
            <div v-if="isLogged" class="columns">
                <div class="column">
                    <div class="buttons p-2">
                        <button
                            v-if="!isSharingDocument"
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireShareDocument"
                        >
                            <Icon family="fal" name="fa-file-upload" />
                        </button>
                        <button
                            v-if="isSharingDocument"
                            class="
                                button
                                is-danger is-outlined is-radiusless
                                mr-1
                            "
                            @click="stopShareDocument"
                        >
                            <Icon family="fal" name="fa-times" />
                        </button>

                        <button
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireTextField"
                        >
                            <Icon family="fal" name="fa-text" />
                        </button>
                        <button
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireDateField"
                        >
                            <Icon family="fal" name="fa-calendar" />
                        </button>
                        <button
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireFileField"
                        >
                            <Icon family="fal" name="fa-paperclip" />
                        </button>
                        <button
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requirePhotoField"
                        >
                            <Icon family="fal" name="fa-camera" />
                        </button>
                        <button
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireSignField"
                            :disabled="!isSharingDocument"
                        >
                            <Icon familt="fal" name="fa-signature" />
                        </button>
                        <button
                            v-if="!isSharingSurvey"
                            class="
                                button
                                is-secondary is-outlined is-radiusless
                                mr-1
                            "
                            @click="requireGetSurvey"
                        >
                            <Icon family="fal" name="fa-poll-h" />
                        </button>
                        <button
                            v-if="isSharingSurvey"
                            class="
                                button
                                is-danger is-outlined is-radiusless
                                mr-1
                            "
                            @click="stopShareSurvey"
                        >
                            <Icon family="fal" name="fa-times" />
                        </button>
                    </div>
                </div>
                <div class="column">
                    <div class="buttons is-right p-2">
                        <button
                            class="
                                button
                                is-primary is-outlined is-radiusless
                                mr-1
                            "
                            @click="toggleHistorical"
                        >
                            <Icon family="fal" name="fa-history" />
                        </button>
                        <button
                            class="
                                button
                                is-primary is-outlined is-radiusless
                                mr-1
                            "
                            @click="toggleNotes"
                        >
                            <Icon family="fal" name="fa-sticky-note" />
                        </button>
                        <button
                            class="
                                button
                                is-primary is-outlined is-radiusless
                                mr-1
                            "
                            @click="toggleChat"
                        >
                            <Icon family="fal" name="fa-comment-lines" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <Settings @save="saveSettings" />
        <PrepareActions
            :participants="participants"
            :signalRSocket="signalRSocket"
            :actualAction="actualAction"
            :roomId="roomId"
            @close="actualAction = null"
        />
        <RequiredActions :signalRSocket="signalRSocket" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";

import Clock from "@/components/Clock.vue";
import Participant from "./Participant";
import LocalParticipant from "./LocalParticipant";

import Historical from "./Historical.vue";
import ColaborativeBoard from "./ColaborativeBoard";
import Notes from "./notes/NotesList";
import Settings from "@/components/settings/Settings.vue";

import RequiredActions from "./actions/BaseRequireAction";
import PrepareActions from "./actions/BasePrepareAction";

import Twilio from "twilio-video";

export default {
    name: "conference",
    components: {
        Clock,
        Settings,
        RequiredActions,
        PrepareActions,
        Participant,
        LocalParticipant,
        Historical,
        ColaborativeBoard,
        Notes,
    },
    props: {
        user: { type: Object, required: true },
        connections: { type: Object, required: true },
        roomId: { type: String, required: true },
        signalRSocket: { type: Object, required: true },
    },
    data() {
        return {
            actualAction: null,

            room: null,
            localTracks: false,
            localParticipant: null,

            participants: [],
            mutedAudio: false,
            mutedVideo: false,
        };
    },
    mounted() {
        this.signalRSocket.on(
            "StopShareDocument",
            this.signalRStopShareDocument
        );
    },
    created() {
        this.createChatRoom();
    },
    beforeDestroy() {
        this.localTracks.forEach(function (track) {
            track.stop();
        });
        this.room.disconnect();
    },
    computed: {
        ...mapGetters("chat", ["isChatOpen", "isUsersOpen"]),
        ...mapGetters("notes", ["isOpenedNotes"]),
        ...mapGetters("conference", [
            "getMediaStreamConstraints",
            "isOpenedHistorical",
            "getOtherUsers",
        ]),
        ...mapGetters("colaborativeBoard", ["getFileId", "getSurveyId"]),
        ...mapGetters("oidcStore", ["oidcIsAuthenticated"]),
        isLogged() {
            return this.oidcIsAuthenticated;
        },
        isSharingDocument() {
            return this.getFileId !== null;
        },
        isSharingSurvey() {
            return this.getSurveyId !== null;
        },
    },
    methods: {
        ...mapActions("chat", ["toggleChat", "toggleUsersList", "addMessage"]),
        ...mapActions("notes", ["toggleNotes"]),
        ...mapActions("conference", [
            "toggleSettings",
            "toggleHistorical",
            "toggleSidebar",
            "setParticipants",
        ]),
        ...mapActions("colaborativeBoard", ["closeDocument", "closeSurvey"]),
        leaveRoomIfJoined() {
            if (this.room) this.room.disconnect();
        },
        async createChatRoom() {
            let tokenResponse = await this.getAccessToken();
            let token = tokenResponse.data.token;

            this.leaveRoomIfJoined();

            Twilio.logLevel = "debug";

            let localTracks = await Twilio.createLocalTracks(
                this.getMediaStreamConstraints
            );

            this.room = await Twilio.connect(token, {
                name: this.roomId,
                tracks: localTracks,
            });

            this.localTracks = localTracks;
            this.localParticipant = this.room.localParticipant;

            this.room.participants.forEach(this.participantConnected);
            this.room.on("participantConnected", this.participantConnected);
            this.room.on(
                "participantDisconnected",
                this.participantDisconnected
            );
            this.room.once("disconnected", (error) => {
                this.$log(error.message);
                this.room.participants.forEach(this.participantDisconnected);
            });
        },
        participantDisconnected(participant) {
            this.$log(`Participant disconnected: ${participant.identity}`);

            this.participants = this.participants.filter(
                (p) => p.identity !== participant.identity
            );

            this.setParticipants(this.participants);
        },
        participantConnected(participant) {
            this.$log(`Participant conected: ${participant.identity}`);

            this.participants.push(participant);

            this.setParticipants(this.participants);
        },
        trackSuscribed(container, track) {
            container.appendChild(track.attach());
        },
        trackUnsubscribed(track) {
            track.detach().forEach((element) => element.remove());
        },
        async getAccessToken() {
            return await axios.get(
                `${process.env.VUE_APP_BACKEND_API_URL}token/${this.roomId}/${this.user.id}`
            );
        },
        async enableRecording(sid, trackId) {
            return await axios.post(
                `${process.env.VUE_APP_BACKEND_API_URL}record/start/${sid}/${trackId}`
            );
        },
        async disabledRecording(sid) {
            return await axios.post(
                `${process.env.VUE_APP_BACKEND_API_URL}record/stop/${sid}`
            );
        },
        requireTextField() {
            this.actualAction = "text";
        },
        requireFileField() {
            this.actualAction = "file";
        },
        requirePhotoField() {
            this.actualAction = "photo";
        },
        requireSignField() {
            this.actualAction = "sign";
        },
        requireShareDocument() {
            this.actualAction = "share-document";
        },
        requireDateField() {
            this.actualAction = "date";
        },
        requireGetSurvey() {
            this.actualAction = "get-survey";
        },
        async stopShareDocument() {
            // Invoke the close for all
            await this.signalRSocket.invoke(
                "StopShareDocument",
                this.roomId,
                this.user.id
            );
            this.addMessage({
                notification: `Se ha detenido la compartición de documento.`,
            });
        },
        stopShareSurvey() {
            this.closeSurvey();
        },
        signalRStopShareDocument() {
            this.closeDocument();
        },
        saveSettings() {
            this.toggleSettings();
        },
        getUser(identity) {
            let users = this.getOtherUsers;
            let user = users.find((u) => u.id === identity);
            return user;
        },
        getUserName(identity) {
            let user = this.getUser(identity);
            if (user === undefined) {
                return "";
            }
            return user.name;
        },
        getUserIdentification(identity) {
            let user = this.getUser(identity);
            if (user === undefined) {
                return "";
            }
            return user.userIdentification;
        },
        muteAudio() {
            this.mutedAudio = true;
        },
        unmuteAudio() {
            this.mutedAudio = false;
        },
        muteVideo() {
            this.mutedVideo = true;
        },
        unmuteVideo() {
            this.mutedVideo = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.videos-container {
    height: 77vh;
    max-height: 77vh;
    overflow-y: auto;
}
</style>